import { type ReactNode, createContext, useContext } from 'react';

import { cn } from '@/lib/utils';

/**
 * Whether the nearest `PreviewStage` should skip its own recessed
 * background — see `PreviewStageFlat` below for why this exists and who
 * uses it. Context, not a prop, because every `PreviewStage` call site that
 * needs this (three provider authoring pages, each passing their own
 * `className`) is a file this feature is explicitly not allowed to edit;
 * a context reads through them without touching a line they own.
 */
const PreviewStageFlatContext = createContext(false);

/**
 * Marks every `PreviewStage` rendered inside `children` as already sitting
 * on its host's own neutral canvas — used by `PaidAdPreviewContent`
 * (`PaidAdPreviewRail.tsx`), whose `aside` is that canvas already
 * (`bg-muted/15` in `full-screen-split-dialog.tsx`).
 *
 * Without this, the paid-ad preview rail nested TWO frames around one ad
 * unit: the aside's tint, then `PreviewStage`'s own `bg-preview-stage`
 * rounded box (a second, tighter mat) around a card that already carries
 * its OWN shadow (`LinkedInAdPreview`/`MetaAdPreview`'s `shadow-elevation-1`
 * — see those files' header comments on why that shadow is the platform's
 * real, sourced frame and not something to remove). Two nested "this is a
 * frame" cues reading as one ad card was the founder's "double frame"
 * screenshot. Flattening `PreviewStage` here removes the REDUNDANT mat and
 * leaves exactly one frame: the ad card's own shadow. Every other
 * `PreviewStage` caller (Briefs, Concept Workbench, the style guide, the
 * onboarding Ready step, chat tool-views) sits directly on a plain page
 * background with no tint of its own, so the mat there is the ONLY stage
 * cue and must stay — this context defaults to `false` and only the rail
 * opts in.
 */
export function PreviewStageFlat({ children }: { children: ReactNode }): JSX.Element {
  return <PreviewStageFlatContext.Provider value>{children}</PreviewStageFlatContext.Provider>;
}

/**
 * The neutral surface a platform replica sits ON.
 *
 * A replica is a card. Put a card straight onto the page and it bleeds into
 * it — in light mode `--background` is 99% against `--card`'s 100%, a
 * one-point gap that measured a ~3/255 spread and made the nesting
 * invisible. `--preview-stage` exists for exactly this and is the only token
 * that reads as "underneath" in BOTH themes (`--muted` inverts between them,
 * so in dark it sits LIGHTER than the card and the nesting reads backwards).
 *
 * This was five hand-rolled divs before — one private `PreviewStage` in
 * `AdUnitPreview`, and four separate `flex justify-center bg-preview-stage`
 * copies in the style guide, the chat tool-view, the Meta authoring page,
 * and nothing at all in the Concept Workbench, which is how that surface
 * ended up with no stage at all. One component, one place to change it.
 *
 * 🔴 Note what this deliberately does NOT use: the `.preview-stage` utility
 * class in `src/index.css`. That one also sets `container-type: size`, which
 * collapses an auto-height child to zero — safe inside the inspector's
 * pre-sized stage, fatal in normal flow. In flow layout it is
 * `bg-preview-stage`, always.
 *
 * Renders WITHOUT the `bg-preview-stage` mat when nested inside
 * `PreviewStageFlat` — see that component's comment.
 *
 * 🔴 Flat mode also drops PADDING, not just the mat. Every one of this
 * feature's three authoring-page call sites passes its own `className`
 * (typically `rounded-xl p-3`, inherited from before `PreviewStageFlat`
 * existed), which `cn`'s tailwind-merge keeps because caller classes win
 * ties against this component's own base classes. With no `bg-preview-stage`
 * behind it, that padding has nothing left to reserve space FOR — it just
 * became a bare, invisible ~12px gap between the rail's own canvas and the ad
 * card's own shadow, which is what the founder's screenshot showed. Forcing
 * `p-0` LAST when flat wins that same tailwind-merge tie regardless of
 * whatever padding a caller's `className` carries, so every current and
 * future flat call site is fixed here without an edit at the call site.
 */
export function PreviewStage({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}): JSX.Element {
  const flat = useContext(PreviewStageFlatContext);
  return (
    <div
      className={cn(
        'flex justify-center p-4',
        !flat && 'bg-preview-stage',
        className,
        flat && 'p-0',
      )}
    >
      {children}
    </div>
  );
}
